import React, { useState } from "react";
import { IngredientDetector } from "./IngredientDetector";
import { ExtractIngredientsVision } from "./ExtractIngredientsVision";
import { PredictIngredients } from "./PredictIngredients";
import { useAuth } from "../contexts/AuthContext";
import { getUserAccessStatus } from "../utils";

interface SectionHeaderProps {
    title: string;
    description: string;
    isExpanded: boolean;
    onToggle: () => void;
    icon: string;
    disabled?: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
    title,
    description,
    isExpanded,
    onToggle,
    icon,
    disabled = false,
}) => (
    <div
        onClick={disabled ? undefined : onToggle}
        style={{
            padding: '20px',
            backgroundColor: disabled ? '#f9fafb' : '#f3f4f6',
            borderRadius: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            marginBottom: isExpanded ? '16px' : '0',
            border: '1px solid #e5e7eb',
            opacity: disabled ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
            if (!disabled) {
                e.currentTarget.style.backgroundColor = '#e5e7eb';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }
        }}
        onMouseLeave={(e) => {
            if (!disabled) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }
        }}
    >
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        }}>
            <div style={{ flex: 1 }}>
                <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: disabled ? '#9ca3af' : '#111827',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                }}>
                    <span style={{ fontSize: '24px' }}>{icon}</span>
                    {title}
                    {disabled && (
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            marginLeft: '8px',
                        }}>
                            Premium Only
                        </span>
                    )}
                </h3>
                <p style={{
                    fontSize: '14px',
                    color: disabled ? '#9ca3af' : '#6b7280',
                    lineHeight: '1.5',
                }}>
                    {description}
                </p>
            </div>
            <div style={{
                fontSize: '24px',
                color: disabled ? '#d1d5db' : '#9ca3af',
                transition: 'transform 0.3s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
                ▼
            </div>
        </div>
    </div>
);

export const OtherTools: React.FC = () => {
    const { dbUser } = useAuth();
    const [expandedSection, setExpandedSection] = useState<string | null>(null);

    // Check if user has premium access
    const accessStatus = getUserAccessStatus(dbUser);
    const isPremium = accessStatus.isPremium || !!dbUser?.passioapikey;

    const toggleSection = (section: string) => {
        if (!isPremium) return; // Don't allow expansion for non-premium users
        setExpandedSection(expandedSection === section ? null : section);
    };

    const sections = [
        {
            id: 'ingredient-detector',
            title: 'Ingredient Scanner',
            description: `Scan your pantry, fridge, or grocery items to automatically detect all visible ingredients from photos or videos. 
            You can copy and paste the list of ingredients you have to give to a meal plan generation!`,
            icon: '🎯',
            component: <IngredientDetector />,
        },
        {
            id: 'vision-extractor',
            title: 'Visual Food Analyzer',
            description: 'Upload an image or video of any meal, recipe, or food items to instantly extract all ingredients with accurate portions, nutrition data, and links to our comprehensive food database.',
            icon: '🔍',
            component: <ExtractIngredientsVision />,
        },
        {
            id: 'predict-ingredients',
            title: 'Predict Next Ingredients',
            description: 'Enter a list of ingredients and we will predict the next ingredient you will need to complete the recipe.',
            icon: '✍️',
            component: <PredictIngredients />,
        },
    ];

    return (
        <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '24px',
        }}>
            <div style={{
                marginBottom: '32px',
                textAlign: 'center',
            }}>
                <h1 style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    color: '#111827',
                    marginBottom: '12px',
                }}>
                    🛠️ AI-Powered Food Tools
                </h1>
                <p style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    maxWidth: '600px',
                    margin: '0 auto',
                    lineHeight: '1.6',
                }}>
                    Powerful AI tools to help you analyze food, extract recipes, and manage your ingredients with ease.
                </p>
                {!isPremium && (
                    <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #fcd34d',
                        maxWidth: '500px',
                        margin: '20px auto 0',
                    }}>
                        <p style={{
                            fontSize: '14px',
                            color: '#92400e',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}>
                            <span style={{ fontSize: '18px' }}>🔒</span>
                            Upgrade to Premium to access these advanced AI tools
                        </p>
                    </div>
                )}
            </div>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
            }}>
                {sections.map((section) => (
                    <div key={section.id}>
                        <SectionHeader
                            title={section.title}
                            description={section.description}
                            isExpanded={expandedSection === section.id}
                            onToggle={() => toggleSection(section.id)}
                            icon={section.icon}
                            disabled={!isPremium}
                        />
                        
                        {expandedSection === section.id && isPremium && (
                            <div style={{
                                padding: '24px',
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                animation: 'slideDown 0.3s ease-out',
                            }}>
                                <style>
                                    {`
                                        @keyframes slideDown {
                                            from {
                                                opacity: 0;
                                                transform: translateY(-10px);
                                            }
                                            to {
                                                opacity: 1;
                                                transform: translateY(0);
                                            }
                                        }
                                    `}
                                </style>
                                {section.component}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}; 